/* @flow */

import clsx from 'clsx';
import fscreen from 'fscreen';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LEFT, RIGHT } from 'react-swipeable';

import type { SwipeEventData } from '../../swipeable/types';

import { logWarning } from '../../../helpers/debug/debug';
import { SettledPromiseFulfilled, fireAndForget } from '../../../helpers/jsHelpers/promise';
import Localizer from '../../../helpers/localization';
import { getImageSourceUrl } from '../../../redux/netgemApi/actions/v8/metadataImage';
import { PictoArrowLeft, PictoArrowRight, PictoX } from '../../pictos/Element';
import Swipeable from '../../swipeable/swipeable';
import './GameSlideshow.css';
import GameSlideShowNavigator from './GameSlideShowNavigator';

const HD_WIDTH = 1280;
const HD_HEIGHT = 720;

type GameSlideshowPropType = {
  +imageIds: Array<string>,
  +title: string | null,
};

const GameSlideshow = ({ imageIds, title }: GameSlideshowPropType): React.Node => {
  const dispatch = useDispatch();

  const abortController = useRef<AbortController>(new AbortController());
  const slideshowRef = useRef<HTMLElement | null>(null);

  const [index, setIndex] = useState<number>(0);
  const [imageUrls, setImageUrls] = useState<Array<string>>([]);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const hasMultipleImages = imageIds.length > 1;

  const onFullscreenChange = useCallback(() => {
    if (!slideshowRef.current || !fscreen.fullscreenEnabled) {
      return;
    }

    if (fscreen.fullscreenElement !== null) {
      // Fullscreen activated
      setIsFullscreen(true);
    } else {
      // Fullscreen deactivated
      setIsFullscreen(false);
    }
  }, [slideshowRef.current]);

  useEffect(() => {
    fscreen.addEventListener('fullscreenchange', onFullscreenChange, { passive: true });

    return () => {
      fscreen.removeEventListener('fullscreenchange', onFullscreenChange, { passive: true });
    };
  }, [onFullscreenChange]);

  useEffect(() => {
    const load = async () => {
      const { signal } = abortController.current;

      const promises: Array<Promise<string>> = imageIds.map((imageId) => dispatch(getImageSourceUrl(imageId, HD_WIDTH, HD_HEIGHT)));
      const results = await Promise.allSettled(promises);
      if (signal.aborted) {
        return;
      }

      const urls: Array<string> = [];
      results.forEach(({ status, value }) => {
        if (status === SettledPromiseFulfilled && typeof value === 'string') {
          urls.push(value);
        }
      });

      setImageUrls(urls);

      // Preload images
      urls.forEach((url) => {
        const i = new Image();
        i.src = url;
      });
    };

    if (imageIds.length > 0) {
      fireAndForget(load(), 'GameSlideshow.load');
    }
  }, [imageIds]);

  const handlePreviousButtonOnClick = useCallback(() => {
    setIndex((prev) => (prev - 1 + imageIds.length) % imageIds.length);
  }, [imageIds]);

  const handleNextButtonOnClick = useCallback(() => {
    setIndex((prev) => (prev + 1) % imageIds.length);
  }, [imageIds]);

  const handleOnSwiped = useCallback(
    (eventData: SwipeEventData) => {
      const { dir } = eventData;

      if (dir === LEFT) {
        handleNextButtonOnClick();
      } else if (dir === RIGHT) {
        handlePreviousButtonOnClick();
      }
    },
    [handleNextButtonOnClick, handlePreviousButtonOnClick],
  );

  const handleImageOnDoubleClick = useCallback(() => {
    if (!slideshowRef.current || !fscreen.fullscreenEnabled) {
      return;
    }

    if (fscreen.fullscreenElement === null) {
      fscreen.requestFullscreen(slideshowRef.current).catch((error) => logWarning(`Fullscreen unavailable at the moment (${error.message})`));
    } else {
      fscreen.exitFullscreen();
    }
  }, []);

  return (
    <Swipeable onSwiped={handleOnSwiped} trackMouse>
      <div className={clsx('gameSlideshow', isFullscreen && 'fullscreen')} ref={slideshowRef}>
        {imageUrls.map((url, i) => (
          <img
            alt={Localizer.localize('accessibility.gaming.image_for_title_and_index', { index: i + 1, title: title ?? '' })}
            className={clsx('gameImage', i === index && 'selected')}
            draggable={false}
            key={url}
            onDoubleClick={handleImageOnDoubleClick}
            src={url}
          />
        ))}
        {isFullscreen ? <PictoX aria-label={Localizer.localize('accessibility.gaming.exit_fullscreen')} className='fullscreenExit' onClick={handleImageOnDoubleClick} /> : null}
        {hasMultipleImages ? (
          <>
            <button aria-label={Localizer.localize('accessibility.gaming.previous_image')} className='imageNav previous' onClick={handlePreviousButtonOnClick} type='button'>
              <PictoArrowLeft forceHoverEffect />
            </button>
            <button aria-label={Localizer.localize('accessibility.gaming.next_image')} className='imageNav next' onClick={handleNextButtonOnClick} type='button'>
              <PictoArrowRight forceHoverEffect />
            </button>
            <GameSlideShowNavigator count={imageUrls.length} currentIndex={index} indexSelected={setIndex} />
          </>
        ) : null}
      </div>
    </Swipeable>
  );
};

export default GameSlideshow;
